import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Box, Button } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'src/hooks/useDebounce';
import { selectBusinessQueryOptions } from 'src/store/selectors/businessSelectors';
import SearchBar from 'src/components/SearchBar';
import Filter from 'src/components/Filter/Filter';
import FILTER_TYPES from 'src/utils/constants/filter';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import {
  downloadBusinessMetrics,
  loadBusinessMetricsByPeriod,
  SAVE_BUSINESS_OPTIONS
} from 'src/store/actions/businessActions';
import { MetricsPeriodOptions, QuoteTrimesterField } from 'src/utils/constants/business';
import DownloadButton from 'src/components/DownloadButton';
import useStyles from './styles';

const quoteStatusFilter = {
  title: 'BUSINESS_VIEW.METRICS.QUOTE_STATUS',
  controlGroup: FILTER_TYPES.RADIO_BUTTON,
  value: 'quoteStatus',
  items: [
    { label: 'BUSINESS_VIEW.METRICS.ACCEPTED', value: QuoteTrimesterField.ACCEPTED },
    { label: 'BUSINESS_VIEW.METRICS.IN_DRAFT', value: QuoteTrimesterField.IN_DRAFT },
    { label: 'BUSINESS_VIEW.METRICS.IN_REVIEW', value: QuoteTrimesterField.IN_REVIEW },
    { label: 'BUSINESS_VIEW.METRICS.REJECTED', value: QuoteTrimesterField.REJECTED },
    { label: 'BUSINESS_VIEW.METRICS.ARCHIVED', value: QuoteTrimesterField.ARCHIVED }
  ]
};

const Toolbar = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectBusinessQueryOptions);
  const { period } = queryOptions;
  const [searchValue, setSearchValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const prevFilterValuesRef = React.useRef({});

  const debounceSearch = useDebounce(
    (name, queryOpts) =>
      dispatch(loadBusinessMetricsByPeriod({ ...queryOpts, name, page: 0 })),
    400
  );

  const handleExcelExport = useCallback(() => {
    dispatch(downloadBusinessMetrics());
  }, [dispatch]);

  const handleSearch = useCallback(
    e => {
      const { value } = e.target;
      setSearchValue(value);
      debounceSearch(value, queryOptions);
    },
    [setSearchValue, debounceSearch, queryOptions]
  );

  const handleClickFilter = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleFilterChange = useCallback(
    event => {
      const prev = prevFilterValuesRef.current;
      prevFilterValuesRef.current = event || {};
      setFilterValues(event || {});

      const date = event?.date;
      const quoteStatus = event?.quoteStatus;
      const dateChanged = date !== prev.date;
      const quoteStatusChanged = quoteStatus !== prev.quoteStatus;

      if (quoteStatusChanged) {
        dispatch({ type: SAVE_BUSINESS_OPTIONS, options: { quoteStatus } });
      }

      if (dateChanged) {
        if (date) {
          const year = momentLocalTimezone(date).year();
          const month =
            period === MetricsPeriodOptions.MONTH
              ? momentLocalTimezone(date).month() + 1
              : undefined;
          dispatch(loadBusinessMetricsByPeriod({ year, month, page: 0 }));
        } else {
          dispatch(
            loadBusinessMetricsByPeriod({
              year: undefined,
              month: undefined,
              page: 0
            })
          );
        }
      }
    },
    [dispatch, period]
  );

  const filters = useMemo(() => {
    if (period === MetricsPeriodOptions.HISTORIC) {
      return undefined;
    }
    const dateFilter = {
      title:
        period === MetricsPeriodOptions.MONTH ? 'COMMON.MONTH' : 'COMMON.YEAR',
      controlGroup: FILTER_TYPES.DATE,
      items: [
        {
          label:
            period === MetricsPeriodOptions.MONTH
              ? 'COMMON.MONTH'
              : 'COMMON.YEAR',
          value: 'date',
          view:
            period === MetricsPeriodOptions.MONTH ? ['month', 'year'] : ['year']
        }
      ]
    };
    if (period === MetricsPeriodOptions.QUARTER) {
      return [dateFilter, quoteStatusFilter];
    }
    return [dateFilter];
  }, [period]);

  return (
    <>
      <Box className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb doNotRedirectIndexList={[0]} />
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            style={{ fontWeight: '400' }}
            color="primary"
            variant="h1"
          >
            {t('NAVIGATION.METRICS')}
          </Typography>
          <Box className={classes.actionsContainer}>
            <SearchBar handleSearch={handleSearch} searchValue={searchValue} />
            {filters && (
              <>
                <Button
                  className={clsx(
                    classes.buttons,
                    classes.outlined,
                    classes.horizontalPadding
                  )}
                  color="primary"
                  onClick={handleClickFilter}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  <FilterList className={classes.icon} />
                  {t('COMMON.FILTER')}
                </Button>
                <Filter
                  anchorEl={anchorEl}
                  filterValues={filterValues}
                  handleClose={handleCloseFilter}
                  handleChange={handleFilterChange}
                  filters={filters}
                />
              </>
            )}
            <DownloadButton
              onDownload={handleExcelExport}
              title={t('COMMON.DOWNLOAD_METRICS')}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
